import { useEffect, useState } from 'react'
import usePageTitle from '@/lib/usePageTitle'

/* Gedeeld frame voor admin-schermen: donker, groot, met titel en live-klok.
   Bedoeld voor een monitor waar een admin naar kijkt tijdens de LAN.
   Kinderen vullen het middenblok — dit component regelt frame + auto-refresh. */
export default function AdminScreenShell({
  title, subtitle, icon: Icon, accent: propAccent = '#B4FF39',
  refreshMs = 15_000, onRefresh, children,
}) {
  const [clock, setClock] = useState(() => new Date())
  const [lastRefresh, setLastRefresh] = useState(null)
  usePageTitle(title)

  // Als het scherm-frame ons z'n eigen kleur meegeeft via ?accent=, gebruik die.
  const accent = (() => {
    try {
      const u = new URL(window.location.href)
      const a = u.searchParams.get('accent')
      return /^#[0-9a-f]{3,8}$/i.test(a || '') ? a : propAccent
    } catch { return propAccent }
  })()

  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000)
    return () => clearInterval(t)
  }, [])

  useEffect(() => {
    if (!onRefresh) return
    let cancelled = false
    const doRefresh = async () => {
      try { await onRefresh(); if (!cancelled) setLastRefresh(new Date()) } catch {}
    }
    doRefresh()
    const t = setInterval(doRefresh, refreshMs)
    return () => { cancelled = true; clearInterval(t) }
  }, [onRefresh, refreshMs])

  return (
    <div className="min-h-screen bg-background text-foreground p-6 flex flex-col">
      <div className="flex items-center gap-4 mb-4 pb-4 border-b border-border/40">
        {Icon && (
          <div className="h-14 w-14 rounded-[6px] border border-border grid place-items-center shrink-0"
               style={{ background: `${accent}18` }}>
            <Icon className="h-8 w-8" style={{ color: accent }} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
            Admin-scherm
          </div>
          <div className="text-3xl font-bold tracking-tight" style={{ color: accent }}>{title}</div>
          {subtitle && <div className="text-sm text-muted-foreground mt-0.5">{subtitle}</div>}
        </div>
        <div className="text-right shrink-0">
          <div className="text-4xl font-bold tabular-nums">
            {clock.toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            {lastRefresh
              ? `laatst ververst ${lastRefresh.toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`
              : 'nog niet ververst'}
          </div>
        </div>
      </div>

      <div className="flex-1">{children}</div>
    </div>
  )
}

/* Nette blok met titel + waarde — herbruikbaar voor stat-tiles. */
export function Tegel({ label, waarde, sub, kleur = '#B4FF39', groot = false }) {
  return (
    <div className="rounded-[6px] border border-border bg-card p-4"
         style={{ borderLeft: `3px solid ${kleur}` }}>
      <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`font-bold tabular-nums leading-none mt-1 ${groot ? 'text-6xl' : 'text-4xl'}`}
           style={{ color: kleur }}>
        {waarde}
      </div>
      {sub && <div className="text-xs text-muted-foreground mt-1.5">{sub}</div>}
    </div>
  )
}

/* Voor lijstjes met status-badges (servers, containers, verbindingen). */
export function StatusRij({ naam, sub, status, kleur }) {
  const statusKleur = kleur ?? (status === 'ok' ? '#61E294' : status === 'warn' ? '#FFB57A' : '#FF5A5A')
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-border/30 last:border-0">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: statusKleur }} />
        <div className="min-w-0">
          <div className="text-base font-semibold truncate">{naam}</div>
          {sub && <div className="text-xs text-muted-foreground truncate">{sub}</div>}
        </div>
      </div>
      <div className="font-mono text-[11px] uppercase tracking-widest shrink-0" style={{ color: statusKleur }}>
        {status}
      </div>
    </div>
  )
}
